import { useEffect, useRef } from "react";

/**
 * Captures keyboard-wedge scanner input without treating normal form typing as a scan.
 * The scanner is expected to send a short burst of characters followed by Enter.
 */
export function useKeyboardScanner({ enabled, blocked, onScan }: { enabled: boolean; blocked: boolean; onScan: (value: string) => void }) {
  const buffer = useRef("");
  const timer = useRef<number | undefined>(undefined);
  const callback = useRef(onScan);
  const inputRef = useRef<HTMLInputElement>(null);
  callback.current = onScan;

  const clear = () => {
    buffer.current = "";
    if (timer.current !== undefined) window.clearTimeout(timer.current);
    timer.current = undefined;
  };

  useEffect(() => {
    if (!enabled) return;
    const isEditable = (target: EventTarget | null) => {
      const element = target instanceof HTMLElement ? target : null;
      return Boolean(element?.closest("input, textarea, select, [contenteditable=true], [data-dialog=true]"));
    };
    const submitInput = () => {
      const input = inputRef.current;
      const value = input?.value.trim() ?? "";
      if (input) input.value = "";
      clear();
      if (!blocked && value) callback.current(value);
    };
    const input = inputRef.current;
    const onInput = () => {
      if (!input) return;
      if (blocked) {
        input.value = "";
        clear();
        return;
      }
      if (input.value.length > 96) input.value = input.value.slice(0, 96);
      if (timer.current !== undefined) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        if (input) input.value = "";
        clear();
      }, 1800);
    };
    const onInputKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        event.preventDefault();
        submitInput();
      }
    };
    input?.addEventListener("input", onInput);
    input?.addEventListener("keydown", onInputKeyDown);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.target === inputRef.current) return;
      if (blocked || isEditable(event.target)) {
        clear();
        return;
      }
      const target = event.target instanceof HTMLElement ? event.target : null;
      if (event.key === "Tab" || event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key === "Enter") {
        if (target?.closest("button, a") && !buffer.current) return;
        if (buffer.current) {
          event.preventDefault();
          const value = buffer.current;
          clear();
          callback.current(value);
        }
        return;
      }
      if (target?.closest("button, a") && event.key === " ") {
        clear();
        return;
      }
      if (event.key.length !== 1 || event.key === " ") return;
      if (buffer.current.length >= 96) return;
      buffer.current += event.key;
      event.preventDefault();
      if (timer.current !== undefined) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(clear, 1800);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      input?.removeEventListener("input", onInput);
      input?.removeEventListener("keydown", onInputKeyDown);
      clear();
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [blocked, enabled]);

  useEffect(() => {
    if (!enabled || blocked) return;
    inputRef.current?.focus({ preventScroll: true });
  }, [blocked, enabled]);

  return inputRef;
}
